import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import { AlertCircle, FolderPlus } from "react-feather";
import { Page } from "../../layouts/Page";
import { Button } from "../../components/rough/Button";
import { ProtocolCard } from "../../components/rough/Protocol/ProtocolCard";
import { ProtocolCardSkeleton } from "../../components/rough/Protocol/ProtocolCardSkeleton";
import { CreateProtocolDialog } from "../../components/rough/Protocol/CreateProtocolDialog";
import { useProtocols } from "../../data/queries/useProtocols";
import { NextPageWithLayout } from "../_app";
import { getMeFromLocalStorage } from "../../data/auth/getMe";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

type ProtocolsPageProps = {
  me: unknown & { clinicId: string };
};

const ProtocolsPage: NextPageWithLayout<ProtocolsPageProps> = () => {
  const loggedUser = getMeFromLocalStorage();
  const { featureFlags } = useFeatureFlags();
  const { data, loading, error } = useProtocols();
  const protocolItems = data?.protocols.data;
  const [isOpenCreateProtocolDialog, setIsOpenCreateProtocolDialog] = useState(false);
  const router = useRouter();

  const canManageProtocols = featureFlags.ENABLE_MANAGE_COURSES;

  useEffect(() => {
    if (!loggedUser) {
      router.push("/login");
    }
  }, [loggedUser, router]);

  return (
    <>
      <div className={"mx-auto flex w-full max-w-[80rem] flex-col px-24 py-8"}>
        <div className={"flex h-12 items-center justify-between"}>
          <h1 className="leading-0 text-3xl">Protocols</h1>

          {canManageProtocols && (
            <Button
              variant="primary"
              text="Create protocol"
              onClick={() => setIsOpenCreateProtocolDialog(true)}
            />
          )}
        </div>

        {loading ? (
          <div className={"flex flex-col gap-2 py-4"}>
            <ProtocolCardSkeleton />
            <ProtocolCardSkeleton />
            <ProtocolCardSkeleton />
          </div>
        ) : (
          <>
            {error ? (
              <div
                className={
                  "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                }>
                <AlertCircle size="36" strokeWidth="1.25" />
                <p className={"mt-2 text-sm text-gray-600"}>We had an unexpected error</p>
                <p className={"mt-2 text-sm"}>{error?.message}</p>
              </div>
            ) : (
              <>
                {protocolItems && protocolItems.length > 0 ? (
                  <div className={"flex flex-col gap-2 py-4"}>
                    {protocolItems.map((protocol) => (
                      <ProtocolCard
                        key={protocol.id}
                        protocol={protocol}
                        canManage={canManageProtocols}
                      />
                    ))}
                  </div>
                ) : (
                  <div
                    className={
                      "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                    }>
                    <FolderPlus strokeWidth="1.25" size="36" />
                    <p className={"mt-2 text-sm"}>Get started by creating a new protocol.</p>
                    <div className={"mt-4"}>
                      <Button
                        variant="primary"
                        text="Create protocol"
                        disabled={!canManageProtocols}
                        onClick={() => setIsOpenCreateProtocolDialog(true)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {canManageProtocols && (
        <CreateProtocolDialog
          open={isOpenCreateProtocolDialog}
          onOpenChange={setIsOpenCreateProtocolDialog}
        />
      )}
    </>
  );
};

ProtocolsPage.getLayout = function getLayout(page: ReactElement) {
  return <Page>{page}</Page>;
};

export default ProtocolsPage;
